import { GlobeIcon } from "@moznsystems/mozn-ui";
import { ArrowheadDownIcon, useClickAway } from "@rageeb/frontend-lib";
import {
	type FC,
	Fragment,
	type KeyboardEvent,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGUAGES, type LanguageCode } from "@/i18n/constants";
import { useIsI18nEnabled } from "@/i18n/feature-flag";
import { rewriteLocaleInPath } from "@/router/rewrite-locale-in-path";

const LanguageSwitcher: FC = () => {
	const enabled = useIsI18nEnabled();
	const { i18n, t } = useTranslation("layout");
	const navigate = useNavigate();
	const location = useLocation();

	const [isOpen, setIsOpen] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(0);

	const containerRef = useClickAway<HTMLDivElement>(() => setIsOpen(false));
	const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

	const activeLanguage = useMemo(
		() => LANGUAGES.find((l) => l.code === i18n.language),
		[i18n.language],
	);

	const handleSelect = useCallback(
		(code: LanguageCode) => {
			setIsOpen(false);
			if (code === i18n.language) return;
			const nextPath = rewriteLocaleInPath(location.pathname, code);
			void i18n.changeLanguage(code);
			navigate(`${nextPath}${location.search}${location.hash}`);
		},
		[i18n, location.hash, location.pathname, location.search, navigate],
	);

	const openMenu = useCallback(() => {
		const activeIdx = LANGUAGES.findIndex((l) => l.code === i18n.language);
		setFocusedIndex(activeIdx >= 0 ? activeIdx : 0);
		setIsOpen(true);
	}, [i18n.language]);

	const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
		if (
			event.key === "Enter" ||
			event.key === " " ||
			event.key === "ArrowDown"
		) {
			event.preventDefault();
			openMenu();
		}
	};

	const handleItemKeyDown = (
		event: KeyboardEvent<HTMLButtonElement>,
		code: LanguageCode,
	) => {
		switch (event.key) {
			case "Enter":
			case " ":
				event.preventDefault();
				handleSelect(code);
				return;
			case "Escape":
				event.preventDefault();
				setIsOpen(false);
				return;
			case "ArrowDown": {
				event.preventDefault();
				const next = (focusedIndex + 1) % LANGUAGES.length;
				setFocusedIndex(next);
				itemRefs.current[next]?.focus();
				return;
			}
			case "ArrowUp": {
				event.preventDefault();
				const prev = (focusedIndex - 1 + LANGUAGES.length) % LANGUAGES.length;
				setFocusedIndex(prev);
				itemRefs.current[prev]?.focus();
				return;
			}
			default:
				return;
		}
	};

	if (!enabled) return null;

	return (
		<div className="relative me-3" ref={containerRef}>
			<button
				type="button"
				aria-haspopup="menu"
				aria-expanded={isOpen}
				aria-label={t("nav.language_switcher.aria_label")}
				onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
				onKeyDown={handleTriggerKeyDown}
				className="inline-flex h-12 items-center gap-2 rounded-md border-0 bg-white px-3 py-2 text-base font-bold text-[#222230] shadow-[0px_2px_12px_0px_rgba(0,23,46,0.08)] select-none"
			>
				<GlobeIcon className="size-5 text-[var(--c-accent)]" />
				<span>{activeLanguage?.label ?? i18n.language}</span>
				<ArrowheadDownIcon
					className={`text-[#bfbfbf] transition-transform ${
						isOpen ? "rotate-180" : ""
					}`}
				/>
			</button>

			{isOpen && (
				<div
					role="menu"
					className="absolute top-[55px] end-0 z-[2] flex w-[140px] flex-col items-stretch rounded-xl bg-white px-6 py-4 shadow-[0px_12px_30px_0px_rgba(0,23,46,0.15)]"
				>
					{LANGUAGES.map((lang, index) => {
						const isActive = lang.code === i18n.language;
						return (
							<Fragment key={lang.code}>
								{index > 0 && (
									<div
										aria-hidden="true"
										className="my-2 h-px w-full bg-[#d9dbe2] opacity-60"
									/>
								)}
								<button
									type="button"
									role="menuitemradio"
									aria-checked={isActive}
									lang={lang.code}
									tabIndex={focusedIndex === index ? 0 : -1}
									ref={(el) => {
										itemRefs.current[index] = el;
									}}
									onClick={() => handleSelect(lang.code)}
									onKeyDown={(event) => handleItemKeyDown(event, lang.code)}
									onFocus={() => setFocusedIndex(index)}
									className={`flex h-8 cursor-pointer items-center justify-start border-0 bg-transparent p-0 text-start text-sm font-semibold leading-[22px] ${
										isActive ? "text-[var(--c-accent)]" : "text-[#222230]"
									}`}
								>
									{lang.label}
								</button>
							</Fragment>
						);
					})}
				</div>
			)}
		</div>
	);
};

export { LanguageSwitcher };
